import { calcularLiquidacionChile } from "./empleados";
import { liquidoBonoSplit } from "./liquidoBonoSplit";
import { saldoLiquidoPorPeriodo, TOLERANCIA_SALDO_LIQUIDO } from "./pagosEmpleados";
import { remuneracionVigenteParaMes } from "./remuneracionVigente";

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// getPendientesRemuneracion — líneas PENDIENTES de sueldo y bono de un empleado Chile, para TODOS los
// períodos con saldo (no solo el mes de un movimiento). Es el MISMO ensamble que `rrhhEntries`
// (FlujoCajaV2) usa para el panel Futuros/RRHH, extraído a un util reusable (10.1) — en Fase 3
// `rrhhEntries` va a consumir esto mismo, por eso los números deben coincidir hoy.
//
// FUENTES (no se reimplementa nada):
//   • saldo de líquido por período (devengado − pagado, ancla al recibo + allowlist intactas) →
//     `saldoLiquidoPorPeriodo` (`pagosEmpleados.js`). Un adelanto NO es una línea propia: entra como
//     `pagado` (sueldo+adelanto) y REDUCE el saldo de sueldo del período que declara.
//   • split base/bono → `liquidoBonoSplit` con **incluirHaberesBono=true** (all-bonos: bono de vigencia
//     `bonos_fijo/variable` + haberes tipo bono/otro). Esta es la definición CONTABLEMENTE CORRECTA y
//     la que ya usa `rrhhEntries` (un haber-bono es un bono, va a su propia línea y se paga como
//     `tipo='bono'`). Difiere del default vigencia-only de `liquidoBonoSplit` (que usa el CANDADO); son
//     iguales si no hay haber-bono (caso Franco).
//
// Cada período con saldo emite: fila "sueldo" = saldo − bonoNeto (base, ya neto de adelantos) y fila
// "bono" = min(bonoNeto, saldo). El bono se capa al saldo (adelanto imputado primero a la base), IGUAL
// que `rrhhEntries`. Solo emite lo que supera `TOLERANCIA_SALDO_LIQUIDO`.
//
// @param {Object} empleado  { id, egreso_fecha }.
// @param {Object} deps
//   @param {Array} remuneraciones  vigencias del empleado.
//   @param {Array<{periodo_año,periodo_mes,monto,imponible,tipo}>} haberes  haberes del empleado (todos los períodos).
//   @param {Array} pagos  filas de `pagos_empleados` del empleado.
//   @param {(fecha:string)=>number} uf / utm  getters de UF (último día del mes) y UTM del mes.
//   @param {number} añoBase  año actual (se proyecta añoBase y añoBase+1, igual que rrhhEntries).
//   @param {Object|null} comisionGen  resultado YA CALCULADO por el panel padre (comisionPorGeneracion)
//     para el comercial: { empleadoId, hoyYm, overdue, pendientePorMesPago:Map<"YYYY-MM",number> }. Si
//     el empleado seleccionado ES ese comercial, se emiten líneas tipo:'comision' por mes de PAGO
//     (mesPagoYm) — CERO recálculo (10.1). Para ese empleado, además, los haberes tipo 'comision' se
//     EXCLUYEN del devengado del sueldo (espeja esComercialSplit de rrhhEntries) para no doble-contar.
// El renglón base sale como 'sueldo' (incluido el saldo remanente de un mes con recibo previo: puede
// haber N filas 'sueldo' del mismo mes desde que se eliminó el índice único, opción b).
// @returns {Array<{tipo:'sueldo'|'bono'|'comision', periodo_año, periodo_mes, monto, periodoLabel, label}>}
export const getPendientesRemuneracion = (
  empleado,
  { remuneraciones = [], haberes = [], pagos = [], uf, utm, añoBase, comisionGen = null } = {}
) => {
  if (!empleado?.id || !añoBase || typeof uf !== "function" || typeof utm !== "function") return [];

  // ¿El empleado seleccionado es el comercial con comisión ya calculada? La identidad la define el
  // propio comisionGen (empleadoId), no un ID hardcodeado.
  const esComercial = !!(comisionGen && comisionGen.empleadoId && comisionGen.empleadoId === empleado.id);

  const devengados = [];
  const dataPorPeriodo = new Map(); // "año-mes" -> { rem, haberesMes, ultimoDia }
  for (const año of [añoBase, añoBase + 1]) {
    for (let m = 0; m < 12; m++) {
      const periodo = `${año}-${String(m + 1).padStart(2, "0")}`;
      if (periodo < "2026-01") continue; // corte: períodos pre-2026 saldados por saldo inicial (rrhhEntries)
      const mesStr = `${periodo}-01`;
      if (empleado.egreso_fecha && empleado.egreso_fecha < mesStr) continue;
      const rem = remuneracionVigenteParaMes(remuneraciones, año, m + 1);
      if (!rem || Number(rem.sueldo_base) <= 0) continue; // sin vigencia → no devenga ese mes
      const ultimoDia = new Date(año, m + 1, 0).toISOString().split("T")[0];
      const haberesMes = (haberes || [])
        .filter((h) => Number(h.periodo_año) === año && Number(h.periodo_mes) === m + 1)
        // Comercial: la comisión va por su propia línea (comisionGen), NO al devengado del sueldo base
        // (mismo criterio que rrhhEntries, esComercialSplit) → si no, se doble-contaría.
        .filter((h) => !(esComercial && h.tipo === "comision"))
        .map((h) => ({ monto: h.monto, imponible: h.imponible, tipo: h.tipo }));
      const liquido = calcularLiquidacionChile(rem, uf(ultimoDia), 0, utm(ultimoDia), haberesMes).liquido;
      devengados.push({ año, mes: m + 1, devengadoMotor: liquido });
      dataPorPeriodo.set(`${año}-${m + 1}`, { rem, haberesMes, ultimoDia });
    }
  }

  const { subledger } = saldoLiquidoPorPeriodo({ devengados, pagos, empleadoId: empleado.id });
  const out = [];
  for (const row of subledger) {
    if (!(row.saldo > TOLERANCIA_SALDO_LIQUIDO)) continue; // sin saldo pendiente → nada
    const data = dataPorPeriodo.get(`${row.año}-${row.mes}`);
    if (!data) continue;
    const { bonoNeto } = liquidoBonoSplit(data.rem, uf(data.ultimoDia), utm(data.ultimoDia), data.haberesMes, true);
    const bonoCap = Math.min(bonoNeto, row.saldo); // adelanto imputado primero a la base
    const baseSaldo = row.saldo - bonoCap;
    const periodoLabel = `${MESES[row.mes - 1]} ${row.año}`;
    if (baseSaldo > TOLERANCIA_SALDO_LIQUIDO) {
      // El saldo remanente de sueldo del mes ES sueldo, no un adelanto a cuenta de sueldo futuro. Puede
      // haber varias filas tipo='sueldo' del mismo mes (mes pagado en varias transferencias, allowlist
      // neteoMotorDirecto): el índice único ux_pagos_empleados_sueldo_mes se eliminó (opción b) y el
      // candado del modal valida el nuevo sueldo sumando los sueldos previos del mes. En Futuros se
      // muestra como "Saldo MM/AA" (label de display de rrhhEntries), pero el tipo persistido es 'sueldo'.
      out.push({
        tipo: "sueldo",
        periodo_año: row.año,
        periodo_mes: row.mes,
        monto: Math.round(baseSaldo),
        periodoLabel,
        label: `Sueldo ${periodoLabel}`,
      });
    }
    if (bonoCap > TOLERANCIA_SALDO_LIQUIDO) {
      out.push({
        tipo: "bono",
        periodo_año: row.año,
        periodo_mes: row.mes,
        monto: Math.round(bonoCap),
        periodoLabel,
        label: `Bono ${periodoLabel}`,
      });
    }
  }

  // Comisión del comercial: líneas pendientes por MES DE PAGO (= cobro real + 1), reusando
  // comisionGen.pendientePorMesPago YA CALCULADO por el panel padre — CERO recálculo (10.1). Espeja
  // rrhhEntries: cada mes de pago >= hoy emite su pendiente propio; lo VENCIDO (meses de pago pasados y
  // aún impagos) se agrupa en el mes en curso (comisionGen.overdue). El período de la fila ES el mes de
  // pago (decisión Ignacio), no el mes de generación.
  if (esComercial && comisionGen.pendientePorMesPago instanceof Map) {
    const { hoyYm, pendientePorMesPago, overdue: overdueRaw } = comisionGen;
    const overdue = Number(overdueRaw || 0);
    const meses = new Set();
    for (const ym of pendientePorMesPago.keys()) if (ym >= hoyYm) meses.add(ym);
    if (overdue > TOLERANCIA_SALDO_LIQUIDO) meses.add(hoyYm); // el vencido cae en el mes en curso
    for (const ym of meses) {
      const propio = Number(pendientePorMesPago.get(ym) || 0);
      const monto = ym === hoyYm ? propio + overdue : propio;
      if (!(monto > TOLERANCIA_SALDO_LIQUIDO)) continue;
      const [y, mm] = String(ym).split("-").map(Number);
      if (!y || !mm) continue;
      const periodoLabel = `${MESES[mm - 1]} ${y}`;
      out.push({
        tipo: "comision",
        periodo_año: y,
        periodo_mes: mm,
        monto: Math.round(monto),
        periodoLabel,
        label: `Comisión ${periodoLabel}`,
      });
    }
  }

  return out;
};
